import type { CompanyData, PersonData } from "@onestore/api/account"
import type { BasketParamListItem } from "@onestore/api/basket"
import type { HTTP, UserState } from "@onestore/onestore-store-common"
import { HTTP_STATUS } from "@onestore/onestore-store-common/http"
import type { Draft } from "immer"
import { produce } from "immer"
import { STORAGE } from "~/lib/storage"
import LocalStorage from "~/lib/storage/LocalStorage"
import type { OrderActions } from "~/store/order/actions"
import { OrderActionType } from "~/store/order/constants"
import type { CheckoutAction } from "./actions"
import { CheckoutActions } from "./constants"

const userLogin = LocalStorage.get(STORAGE.USER_LOGIN)
const userExists = LocalStorage.getBool(STORAGE.LOGIN_EXISTS)

export type CheckoutState = {
  readonly request_state: HTTP.Status
  readonly user_request: HTTP.Status
  readonly user?: UserState
  readonly login?: string | null
  readonly email?: string | null
  readonly is_after_login?: boolean

  readonly accountExists: boolean
  readonly isEditModalOpen: boolean
  readonly showRegistration: boolean
  readonly userRegistered: boolean
  readonly userUpdated: boolean

  readonly authUserRequest: HTTP.Status

  // Parametry elementów koszyka potrzebne do wypełnienia przez użytkownika
  readonly params: Record<string, BasketParamListItem>
  readonly get_params_request: HTTP.Status
  readonly save_params_request: HTTP.Status

  readonly modal: {
    confirmAddress: {
      isOpen: boolean
      user?: CompanyData | PersonData
    }
  }
}

export const initialState: CheckoutState = {
  user: undefined,
  user_request: HTTP_STATUS.NONE,
  login: userLogin,

  request_state: HTTP_STATUS.NONE,
  showRegistration: false,
  userRegistered: false,
  userUpdated: false,
  isEditModalOpen: false,

  // Dane konta.
  accountExists: null === userExists ? false : userExists,
  authUserRequest: HTTP_STATUS.NONE,

  // Parametry elementów koszyka potrzebne do wypełnienia przez użytkownika
  params: {},
  get_params_request: HTTP_STATUS.NONE,
  save_params_request: HTTP_STATUS.NONE,

  modal: {
    confirmAddress: {
      isOpen: false,
      user: undefined,
    },
  },
}

export default function reducer(
  state: CheckoutState = initialState,
  action: CheckoutAction | OrderActions
): CheckoutState {
  return produce<CheckoutState, CheckoutState>(
    state,
    (nextState: Draft<CheckoutState>): void => {
      switch (action.type) {
        case OrderActionType.ORDER_SUCCESS:
          LocalStorage.setBool(STORAGE.LOGIN_EXISTS, true)
          nextState.accountExists = true
          // Reset a stale BAD_REQUEST left by a previous PAYMENT_FAILURE, so a
          // successful order doesn't leave `checkoutRequestFailed` (-> ErrorInfoModal)
          // reporting an error that no longer applies.
          nextState.request_state = HTTP_STATUS.OK

          return
        // Uwierzytelnienie konta użytkownika

        case CheckoutActions.AUTH_PENDING:
          nextState.authUserRequest = action.status

          return
        case CheckoutActions.AUTH_SUCCESS:
          nextState.login = action.login
          nextState.accountExists = action.accountExists || state.accountExists
          nextState.authUserRequest = action.status

          return
        case CheckoutActions.AUTH_LOGIN_RESET:
          nextState.login = action.login
          nextState.accountExists = action.accountExists
          nextState.authUserRequest = action.authUserRequest
          nextState.showRegistration = false

          return

        case CheckoutActions.AUTH_LOGIN_SKIP:
          nextState.login = action.login
          nextState.accountExists = action.accountExists
          nextState.authUserRequest = action.authUserRequest
          nextState.showRegistration = true

          return

        case CheckoutActions.USER_EDIT_DATA_MODAL_OPEN:
          nextState.isEditModalOpen = true

          return

        case CheckoutActions.USER_EDIT_DATA_MODAL_CLOSE:
          nextState.isEditModalOpen = false

          return
        case CheckoutActions.AUTH_FAILURE:
          nextState.authUserRequest = action.status

          return

        case CheckoutActions.BASKET_PENDING:
          nextState.request_state = HTTP_STATUS.CONTINUE

          return

        case CheckoutActions.BASKET_FAILURE:
          nextState.request_state = HTTP_STATUS.BAD_REQUEST

          return

        case CheckoutActions.USER_REGISTER_SUCCESS:
          nextState.userRegistered = true
          nextState.user = {
            ...action.result,
            tax: "tax" in action.result ? action.result.tax : [],
            company_name:
              "company_name" in action.result ? action.result.company_name : "",
          }

          nextState.login = action.result.login

          return

        case CheckoutActions.USER_UPDATE_SUCCESS:
          nextState.userUpdated = true
          nextState.user = {
            ...action.result,
            tax: "tax" in action.result ? action.result.tax : [],
            company_name:
              "company_name" in action.result ? action.result.company_name : "",
          }

          nextState.login = action.result.login

          return

        case CheckoutActions.USER_PENDING:
          nextState.user_request = HTTP_STATUS.CONTINUE

          return
        case CheckoutActions.USER_FAILURE:
          nextState.user_request = action.response_status_code

          return
        case CheckoutActions.USER_SUCCESS:
          nextState.user_request = action.response_status_code
          nextState.user = action.result
          nextState.email = action.result.email
          nextState.is_after_login = action.is_after_login

          return
        case CheckoutActions.BASKET_SUCCESS:
          nextState.request_state = HTTP_STATUS.OK

          return
        case CheckoutActions.RESET:
          LocalStorage.remove(STORAGE.OLD_ORDER)

          nextState = {
            ...state,
            ...initialState,
          }

          if (!LocalStorage.get(STORAGE.USER_LOGIN)) {
            nextState.login = undefined
          }

          return
        case CheckoutActions.CHECKOUT_RESET:
          nextState.request_state = HTTP_STATUS.NONE
          nextState.user_request = HTTP_STATUS.NONE

          return

        case CheckoutActions.BASKET_PARAMS_GET_PENDING:
          nextState.get_params_request = HTTP_STATUS.CONTINUE

          return
        case CheckoutActions.BASKET_PARAMS_GET_FAILURE:
          nextState.get_params_request = action.response_status_code
          nextState.params = {}

          return
        case CheckoutActions.BASKET_PARAMS_GET_SUCCESS:
          nextState.get_params_request = action.response_status_code
          nextState.params = action.params

          return
        case CheckoutActions.BASKET_PARAMS_SAVE_PENDING:
          nextState.save_params_request = HTTP_STATUS.CONTINUE

          return
        case CheckoutActions.BASKET_PARAMS_SAVE_FAILURE:
          nextState.save_params_request = action.response_status_code
          nextState.params = {}

          return
        case CheckoutActions.BASKET_PARAMS_SAVE_SUCCESS:
          nextState.save_params_request = action.response_status_code

          return

        case CheckoutActions.ADDRESS_CONFIRMATION_CLOSE:
          nextState.modal.confirmAddress.isOpen = false
          nextState.modal.confirmAddress.user = undefined

          return

        case CheckoutActions.ADDRESS_CONFIRMATION_OPEN:
          nextState.modal.confirmAddress.isOpen = true
          nextState.modal.confirmAddress.user = action.user

          return

        case OrderActionType.PAYMENT_FAILURE:
          // `checkout.request_state` feeds `checkoutRequestFailed` (-> ErrorInfoModal)
          // and `getSummaryRequestStatus`. `order.order_request` is the source of truth
          // for order/payment state, but this mirror keeps the generic checkout error
          // modal working on payment failure — do not remove without also updating
          // `checkoutRequestFailed` to read `order.order_request` instead.
          nextState.request_state = HTTP_STATUS.BAD_REQUEST

          return

        case OrderActionType.PAYMENT_PENDING:
        case OrderActionType.PAYMENT_SUCCESS:
          // Reset a stale BAD_REQUEST left by a previous PAYMENT_FAILURE, so a
          // successful retry doesn't leave `checkoutRequestFailed` (-> ErrorInfoModal)
          // reporting an error that no longer applies.
          nextState.request_state = HTTP_STATUS.OK

          return
      }
    }
  )
}
